import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { FETCH_ALLOCATION_RULE, UPDATE_ALLOCATION_RULE } from '../../../api/queries/allocation';
import { stagingOptions } from '../../../constants/apollo';
import {
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Assignment as AssignmentIcon,
  Star as StarIcon,
  Tag as TagIcon,
  History as HistoryIcon,
  Update as UpdateIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  Code as CodeIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  Label as LabelIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Badge } from '../components/ui/Badge';
import { toast } from 'react-toastify';
import { getErrorMessage } from '../utils';
import moment from 'moment';

const PARAM_LABELS: Record<string, string> = {
  'order.neighborhood.name': 'Order Neighborhood Name',
  'order.merchant.id': 'Merchant ID',
  'order.merchant.merchant_type': 'Merchant Type',
};

const OPERATOR_META: Record<
  string,
  { label: string; colorClass: string }
> = {
  eq: {
    label: 'equals',
    colorClass: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  },
  not_eq: {
    label: 'does not equal',
    colorClass: 'bg-rose-50 text-rose-700 border border-rose-200',
  },
  contains: {
    label: 'contains',
    colorClass: 'bg-sky-50 text-sky-700 border border-sky-200',
  },
  gt: {
    label: 'is greater than',
    colorClass: 'bg-orange-50 text-orange-700 border border-orange-200',
  },
  gte: {
    label: 'is greater than or equal to',
    colorClass: 'bg-amber-50 text-amber-700 border border-amber-200',
  },
  lt: {
    label: 'is less than',
    colorClass: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
  },
  lte: {
    label: 'is less than or equal to',
    colorClass: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
  },
};

const RuleDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { data, loading, error } = useQuery(FETCH_ALLOCATION_RULE, {
    ...stagingOptions,
    variables: { id },
    fetchPolicy: 'network-only',
  });

  const rule = data?.fetchAllocationRule;

  const [updateRule, { loading: updating }] = useMutation(UPDATE_ALLOCATION_RULE, {
    ...stagingOptions,
    onCompleted: () => {
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Unable to update rule status.'));
    }
  });

  const handleBack = () => {
    navigate('/allocation');
  };

  const handleToggleRule = async () => {
    if (!rule) return;
    try {
      await updateRule({
        variables: {
          input: {
            id: rule.id,
            isActive: !rule.isActive,
          },
        },
      });
      toast.success(`Rule "${rule.name}" ${!rule.isActive ? 'activated' : 'deactivated'} successfully.`);
    } catch (error) {
      console.error('Failed to toggle rule', error);
    }
  };

  if (loading) {
    return (
      <div className="main-wrapper">
        <div className="w-full px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <CircularProgress size={48} sx={{ color: '#0f62fe' }} />
              <p className="mt-4 text-gray-600 font-medium">Loading allocation rule details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !rule) {
    return (
      <div className="main-wrapper">
        <div className="w-full px-6 lg:px-8 py-8">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-primaryColor transition-colors mb-6 group"
          >
            <ArrowBackIcon sx={{ fontSize: '20px' }} className="group-hover:translate-x-[-2px] transition-transform" />
            <span className="text-sm font-medium">Back to Rules</span>
          </button>
          <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center max-w-3xl mx-auto shadow-sm">
            <p className="text-red-600 font-semibold text-lg mb-2">Unable to load rule details</p>
            <p className="text-red-500 text-sm">
              {error?.message || 'This rule may not exist or there was a problem fetching it.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const conditionCount = rule.conditions ? rule.conditions.length : 0;

  const getStatusColor = (): string => {
    return rule.isActive
      ? 'from-green-500 via-green-600 to-green-700'
      : 'from-primaryColor via-primaryColor to-darkBlue';
  };

  return (
    <div className="flex flex-col space-y-2 min-h-screen bg-gray-50">
      <div className="main-wrapper">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">

          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-primaryColor transition-colors mb-4 group"
          >
            <ArrowBackIcon sx={{ fontSize: '20px' }} className="group-hover:translate-x-[-2px] transition-transform" />
            <span className="text-sm font-medium">Back to Rules</span>
          </button>

          <div className="mb-4">
            <div className={`bg-gradient-to-r ${getStatusColor()} rounded-xl p-4 lg:p-6 shadow-lg relative overflow-hidden`}>
              <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -mr-24 -mt-24" />
              <div className="absolute bottom-0 left-0 w-36 h-36 bg-white/5 rounded-full -ml-18 -mb-18" />

              <div className="relative z-10">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center shadow-lg border border-white/30">
                      <AssignmentIcon sx={{ fontSize: '24px', color: 'white' }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h1 className="text-2xl lg:text-3xl font-bold text-white drop-shadow-lg">
                          {rule.name || `Rule #${rule.id}`}
                        </h1>
                        {rule.ruleType && (
                          <Chip
                            label={rule.ruleType}
                            size="small"
                            className="bg-white/20 text-white border border-white/30"
                            sx={{ fontSize: '0.7rem', height: '22px', fontWeight: 600, color: 'white' }}
                          />
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-white/95">
                        <div className="flex items-center gap-1.5">
                          <StarIcon sx={{ fontSize: '16px' }} />
                          <span className="font-medium">Priority: {rule.priority}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <LabelIcon sx={{ fontSize: '16px' }} />
                          <span className="font-medium">Result via: {rule.resultVia}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <PlaylistAddCheckIcon sx={{ fontSize: '16px' }} />
                          <span className="font-medium">Conditions: {conditionCount}</span>
                        </div>
                      </div>
                      {rule.description && (
                        <p className="mt-2 text-sm text-white/90 max-w-2xl flex items-start gap-1.5">
                          <DescriptionIcon sx={{ fontSize: '14px', marginTop: '3px' }} />
                          {rule.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={rule.isActive}
                          onChange={handleToggleRule}
                          color="primary"
                          size="small"
                          disabled={updating}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#10B981',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#10B981',
                            },
                            '& .MuiSwitch-switchBase': {
                              '&.Mui-disabled': {
                                color: rule.isActive ? '#10B981' : '#fff',
                                opacity: 0.5
                              }
                            }
                          }}
                        />
                      }
                      label={
                        <span className="text-xs font-bold text-white drop-shadow-sm uppercase">
                          {rule.isActive ? 'Active' : 'Inactive'}
                        </span>
                      }
                      sx={{
                        margin: 0,
                        padding: '4px 8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(4px)',
                        borderRadius: '9999px',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        '& .MuiFormControlLabel-label': {
                          marginLeft: '4px',
                        },
                      }}
                    />
                    <Tooltip title="Edit Allocation Rule" arrow>
                      <IconButton
                        onClick={() => navigate(`/allocation/edit/${id}`)}
                        className="bg-white/20 hover:bg-white/30 border border-white/30"
                        sx={{ color: 'white' }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap mb-6">
            <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center shadow-sm">
                <CheckCircleIcon sx={{ fontSize: '20px', color: '#3B82F6' }} />
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">Status</p>
                <p className="text-xl font-bold text-gray-900">{rule.isActive ? 'Active' : 'Inactive'}</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white to-purple-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-100 to-purple-50 flex items-center justify-center shadow-sm">
                <TrendingUpIcon sx={{ fontSize: '20px', color: '#9333EA' }} />
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">Priority Rank</p>
                <p className="text-xl font-bold text-gray-900">{rule.priority}</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white to-green-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center shadow-sm">
                <PlaylistAddCheckIcon sx={{ fontSize: '20px', color: '#10B981' }} />
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">Conditions</p>
                <p className="text-xl font-bold text-gray-900">{conditionCount}</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white to-amber-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-100 to-amber-50 flex items-center justify-center shadow-sm">
                <LabelIcon sx={{ fontSize: '20px', color: '#F59E0B' }} />
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">Result Method</p>
                <p className="text-xl font-bold text-gray-900">{rule.resultVia}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 shadow-md p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primaryColor to-darkBlue flex items-center justify-center shadow-md">
                    <AssignmentIcon sx={{ fontSize: '20px', color: 'white' }} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Rule Configuration</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-blue-200 transition-colors">
                    <div className="flex items-center gap-1.5 mb-1">
                      <TagIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Rule ID</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900 font-mono">#{rule.id}</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-blue-200 transition-colors">
                    <div className="flex items-center gap-1.5 mb-1">
                      <StarIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Execution Priority</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{rule.priority}</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-blue-200 transition-colors">
                    <div className="flex items-center gap-1.5 mb-1">
                      <InfoIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Type</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{rule.ruleType || '—'}</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-blue-200 transition-colors">
                    <div className="flex items-center gap-1.5 mb-1">
                      <LabelIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Result Via</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">
                      <Badge variant="default" className="text-xs">
                        {rule.resultVia}
                      </Badge>
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-blue-200 transition-colors md:col-span-2">
                    <div className="flex items-center gap-1.5 mb-1">
                      <CodeIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Result Data</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900 break-all">{rule.resultData || 'N/A'}</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-blue-200 transition-colors">
                    <div className="flex items-center gap-1.5 mb-1">
                      <HistoryIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Created</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">
                      {rule.createdAt ? moment(rule.createdAt).format('DD/MM/YYYY HH:mm') : 'N/A'}
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-blue-200 transition-colors">
                    <div className="flex items-center gap-1.5 mb-1">
                      <UpdateIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Last Updated</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">
                      {rule.updatedAt ? moment(rule.updatedAt).format('DD/MM/YYYY HH:mm') : 'N/A'}
                    </p>
                  </div>
                </div>

                {rule.conditionsSummary && (
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Conditions Logic Summary</label>
                    <div className="p-4 bg-blue-50/50 border border-blue-100 rounded-xl text-sm text-gray-800 leading-relaxed italic">
                      " {rule.conditionsSummary} "
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-xl border border-gray-200 shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-md">
                      <PlaylistAddCheckIcon sx={{ fontSize: '20px', color: 'white' }} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Rule Conditions</h3>
                    <span className="px-2.5 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold">
                      {conditionCount}
                    </span>
                  </div>
                </div>

                {rule.conditions && rule.conditions.length > 0 ? (
                  <div className="space-y-3">
                    {rule.conditions.map((condition: any, index: number) => {
                      const paramLabel = PARAM_LABELS[condition.param] || condition.param;
                      const opMeta = OPERATOR_META[condition.operator] || OPERATOR_META.eq;

                      return (
                        <div
                          key={`${rule.id}-cond-${index}`}
                          className="group relative overflow-hidden rounded-xl border border-gray-200 p-4 hover:border-primaryColor/30 hover:shadow-md transition-all duration-300"
                        >
                          <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-primaryColor to-darkBlue" />
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className="text-[10px] font-bold text-primaryColor uppercase tracking-widest bg-primaryColor/5 px-2 py-0.5 rounded">
                                  PARAMETER
                                </span>
                                <span className="text-sm font-bold text-gray-900">{paramLabel}</span>
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm text-gray-600 font-medium">When matched with</span>
                                <span className={`px-2 py-0.5 rounded text-[11px] font-bold uppercase ${opMeta.colorClass}`}>
                                  {opMeta.label}
                                </span>
                                <span className="text-sm font-mono font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100">
                                  "{condition.value}"
                                </span>
                                {condition.cast && condition.cast !== 'string' && (
                                  <span className="text-[10px] text-gray-400 font-medium">({condition.cast} cast)</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                    <PlaylistAddCheckIcon sx={{ fontSize: '48px', color: '#9CA3AF', mb: 2 }} />
                    <p className="text-sm font-semibold text-gray-600">No conditions defined</p>
                    <p className="text-xs text-gray-400">This rule will apply generally if no conditions are specified.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="xl:col-span-1 space-y-4">

              <div className="bg-white rounded-xl border border-gray-200 shadow-md p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center">
                    <CodeIcon sx={{ fontSize: '18px', color: 'white' }} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Raw Data</h3>
                </div>

                <div className="p-3 bg-[#0d1117] rounded-xl overflow-hidden border border-gray-800">
                  <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-800">
                    <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">JSON PAYLOAD</span>
                    <Tooltip title="Copy to clipboard" arrow>
                      <IconButton size="small" sx={{ color: 'gray' }}>
                        <CodeIcon sx={{ fontSize: '14px' }} />
                      </IconButton>
                    </Tooltip>
                  </div>
                  <div className="max-h-80 overflow-auto scrollbar-hide">
                    <pre className="text-[11px] font-mono text-green-400 whitespace-pre-wrap leading-relaxed">
                      {JSON.stringify(rule, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RuleDetails;
